import { getPerformanceCategory } from "./helper";

function flattenIssueItems(issue) {
  const out = [];
  const walk = (items) => {
    if (!Array.isArray(items)) return;
    for (const item of items) {
      if (!item || typeof item !== "object") continue;
      out.push(item);
      if (item.subItems?.items) walk(item.subItems.items);
      if (Array.isArray(item.items)) walk(item.items);
    }
  };
  walk(issue?.items);
  if (issue?.details?.items && Array.isArray(issue.details.items)) {
    walk(issue.details.items);
  }
  return out;
}

function normalizeTitle(title) {
  return (title || "")
    .replace(/^\d+\.\s*/, "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
}

function parseWasteKb(text) {
  const m = String(text).match(/wasteKb:\s*(\d+)/i);
  return m ? Number(m[1]) : null;
}

function extractPathHint(text) {
  const paren = String(text).match(
    /\(([a-zA-Z0-9_./%-]+\.(?:avif|webp|png|jpe?g|gif|svg)[^)]*)\)/i,
  )?.[1];
  if (paren) return paren.toLowerCase();

  const src = String(text).match(/\bsrc=["']([^"']+)["']/i)?.[1];
  if (src && !src.includes("...")) return src.toLowerCase();

  return null;
}

function findLighthouseIssue(knownIssues, aiTitle) {
  const entry = findLighthouseIssueEntry(knownIssues, aiTitle);
  return entry?.issue ?? null;
}

export function findLighthouseIssueEntry(knownIssues, aiTitle) {
  const target = normalizeTitle(aiTitle);
  const entries = Object.entries(knownIssues || {});

  const exact = entries.find(([, i]) => normalizeTitle(i.title) === target);
  if (exact) return { key: exact[0], issue: exact[1] };

  const partial = entries.find(([, i]) => {
    const t = normalizeTitle(i.title);
    return t.includes(target) || target.includes(t);
  });
  if (partial) return { key: partial[0], issue: partial[1] };

  return null;
}

function itemWasteKb(item) {
  if (item.wastedBytes != null) return Math.round(item.wastedBytes / 1024);
  return null;
}

function getItemUrl(item) {
  return item.url || item.source?.url || null;
}

function getItemNode(item) {
  const node = item.node;
  if (!node || typeof node !== "object") return null;
  if (node.boundingRect || node.snippet || node.selector) return node;
  return null;
}

function pathMatches(url, hint) {
  if (!url || !hint) return false;
  const lower = url.toLowerCase();
  const normalized = hint.replace(/^\//, "");
  return (
    lower.includes(normalized) || lower.endsWith(normalized.split("/").pop())
  );
}

function matchResourceItem(items, text, index, used) {
  const wasteKb = parseWasteKb(text);
  const pathHint = extractPathHint(text);

  if (wasteKb != null) {
    for (let i = 0; i < items.length; i++) {
      if (used.has(i)) continue;
      const kb = itemWasteKb(items[i]);
      if (kb != null && Math.abs(kb - wasteKb) <= 1) {
        return i;
      }
    }
  }

  if (pathHint) {
    for (let i = 0; i < items.length; i++) {
      if (used.has(i)) continue;
      const url = getItemUrl(items[i]);
      const snippet = items[i].node?.snippet || "";
      if (
        pathMatches(url, pathHint) ||
        snippet.toLowerCase().includes(pathHint.split("/").pop())
      ) {
        return i;
      }
    }
  }

  if (items[index] != null && !used.has(index)) {
    return index;
  }

  for (let i = 0; i < items.length; i++) {
    if (!used.has(i)) return i;
  }

  return -1;
}

function inferSeverityFromFinding(finding) {
  if (!finding) return "needs-improvement";
  const kiB = finding.match(/(\d+)\s*KiB/i);
  if (kiB && Number(kiB[1]) >= 200) return "poor";
  return "needs-improvement";
}

/**
 * Match AI suggestion severity to the Issues tab (Lighthouse audit score).
 * Poor → red triangle; Needs Improvement → orange square.
 */
export function resolveIssueSeverity(issue, knownIssues) {
  const lhIssue = findLighthouseIssue(knownIssues, issue?.title);
  if (lhIssue?.score != null) {
    const category = getPerformanceCategory(lhIssue.score * 100);
    if (category === "Poor") return "poor";
    if (category === "Needs Improvement") return "needs-improvement";
  }
  return inferSeverityFromFinding(issue?.finding);
}

/**
 * Match AI "Affected" strings to Lighthouse issue items (full URL, node, thumbnail rect).
 */
export function enrichAffectedItems(issue, knownIssues) {
  const lhIssue = findLighthouseIssue(knownIssues, issue?.title);
  const items = lhIssue ? flattenIssueItems(lhIssue) : [];
  const used = new Set();

  return (issue?.affected || []).map((text, index) => {
    const matchedIdx = items.length
      ? matchResourceItem(items, text, index, used)
      : -1;

    if (matchedIdx >= 0) used.add(matchedIdx);

    const matched = matchedIdx >= 0 ? items[matchedIdx] : null;

    return {
      text,
      url: matched ? getItemUrl(matched) : null,
      node: matched ? getItemNode(matched) : null,
    };
  });
}
